import api from './api.service';
import type { AiPageContext } from '@/utils/aiPageContextFromPath';

type ApiBody<T> = { success?: boolean; data?: T; message?: string };

async function unwrap<T>(promise: Promise<unknown>): Promise<T> {
  const body = (await promise) as ApiBody<T>;
  if (body?.data === undefined) {
    throw new Error(body?.message || 'Intelligence request failed');
  }
  return body.data;
}

export type UnifiedContextPack = {
  pageContext: AiPageContext & { includeSimulation?: boolean; maskFinancial?: boolean };
  entity: {
    module: string;
    docType?: string;
    docId?: string;
    cacheKey: string;
  };
  summary: Record<string, unknown> | null;
  linkedDocuments: Array<{ type: string; id: string; label?: string }>;
  modulePack: {
    module: string;
    healthScore: number;
    risks: Array<{ id: string; title: string; severity: string; source: string; detail?: string }>;
    recommendations: Array<{ id: string; title: string; rationale: string; source: string }>;
    hints: Record<string, unknown>;
  } | null;
  knowledge: Array<{ id: string; title: string; snippet: string; source: string }>;
  signals: Array<{ id: string; title: string; severity: string; source: string; detail?: string }>;
  predictions: Array<{ id: string; title: string; value?: string; source: string }>;
  recommendations: Array<{ id: string; title: string; rationale: string; source: string; confidence?: number }>;
  actions: Array<{ id: string; title: string; status: string; priority?: string; source: string }>;
  simulations: Array<{ id: string; name: string; scenarioType?: string; status?: string; summary?: string }>;
  meta: {
    trust: string;
    evidence: string[];
    sourcesSucceeded: string[];
    analyzerErrors: string[];
    dataAsOf: string;
    moduleHealth: number;
    degraded: boolean;
    cacheHit?: boolean;
    elapsedMs?: number;
  };
};

export type RelationshipGraph = {
  entityType: string;
  entityId: string;
  nodes: Array<{ id: string; type: string; label: string }>;
  edges: Array<{ from: string; to: string; relation: string }>;
};

function toQuery(ctx: AiPageContext & { includeSimulation?: boolean; maskFinancial?: boolean }) {
  const params: Record<string, string> = {};
  if (ctx.module) params.module = ctx.module;
  if (ctx.docType) params.docType = ctx.docType;
  if (ctx.docId) params.docId = ctx.docId;
  if (ctx.includeSimulation) params.includeSimulation = 'true';
  if (ctx.maskFinancial) params.maskFinancial = 'true';
  if (ctx.filters) params.filters = JSON.stringify(ctx.filters);
  return params;
}

export const intelligenceService = {
  getContext: (ctx: AiPageContext & { includeSimulation?: boolean; maskFinancial?: boolean }) =>
    unwrap<UnifiedContextPack>(api.get('/intelligence/context', { params: toQuery(ctx) })),

  getModuleHealth: () => unwrap<unknown[]>(api.get('/intelligence/module-health')),

  explain: (ctx: AiPageContext) =>
    unwrap<UnifiedContextPack>(api.post('/intelligence/explain', ctx)),

  trackAdoption: async (payload: {
    eventType: string;
    moduleCode?: string;
    docType?: string;
    docId?: string;
    payload?: Record<string, unknown>;
  }) => {
    try {
      await api.post('/intelligence/adoption', payload);
    } catch {
      /* soft-fail adoption */
    }
  },

  getRelationship: (entityType: string, entityId: string) =>
    unwrap<RelationshipGraph>(api.get(`/intelligence/relationship/${entityType}/${entityId}`)),

  simulate: (payload: {
    scenarioType: string;
    title?: string;
    params?: Record<string, unknown>;
    confirm: boolean;
  }) => unwrap<unknown>(api.post('/intelligence/simulate', payload)),

  createAction: (payload: {
    title: string;
    description?: string;
    domain?: string;
    priority?: string;
    docType?: string;
    docId?: string;
    confirm: boolean;
  }) => unwrap<unknown>(api.post('/intelligence/actions', payload)),
};
